/**
 * Module: Workflow Builder Layout Component
 *
 * Full-screen layout for the visual workflow builder.
 * Provides minimal chrome and authentication.
 */

import { motion } from "framer-motion"

/**
 * Props for WorkflowBuilderLayout component
 * Defines children content to be rendered
 */
type WorkflowBuilderLayoutProps = {
  /** Child components to render in the layout */
  children: React.ReactNode
}

/**
 * Workflow builder full-screen layout component
 * Provides authenticated, distraction-free workspace for building workflows
 *
 * Features:
 * - Full viewport height without header or sidebar
 * - Minimal UI chrome for maximum canvas space
 * - Back navigation provided by node palette header
 *
 * @param props - Component props
 * @param props.children - Child components to render
 * @returns Rendered full-screen layout with children
 */
export function WorkflowBuilderLayout({ children }: WorkflowBuilderLayoutProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="h-dvh w-screen overflow-hidden bg-transparent"
    >
      {/* Full-Screen Canvas */}
      <div className="h-full w-full">
        {children}
      </div>
    </motion.div>
  )
}
